import { useEffect, useMemo, useState } from 'react'
import { ManageStagesLink } from '@/components/shared/manage-links'
import { Link, useSearchParams } from 'react-router-dom'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { StageBadge } from '@/components/shared/stage-badge'
import { Download, Loader2, Pencil, Plus, RefreshCw, Trash2, Users, X } from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown, Sparkles } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn, formatNumber, initials } from '@/lib/utils'
import { downloadSegmentCsv, stageBadgeVariant, stageLabel } from '@/hooks/use-contacts'
import { useMyPermissions, useTags } from '@/hooks/use-settings'
import {
  CONDITION_TYPE_LABELS,
  CONTACT_FIELDS,
  SEGMENT_OPERATORS,
  emptyCondition,
  emptyGroup,
  operatorLabel,
  operatorsFor,
  useCdpProperties,
  TAG_OPERATORS,
  useCreateSegment,
  useSegmentAiDraft,
  useDeleteSegment,
  useEventDefinitions,
  useRecalculateSegment,
  useSegmentContacts,
  useSegments,
  useUpdateSegment,
  type Segment,
  type SegmentCondition,
  type SegmentConditionGroup,
  type SegmentConditionType,
  type SegmentContact,
} from '@/hooks/use-cdp'
import { Field, QueryError, formatDateTime } from './cdp-shared'

const CONDITION_TYPES = Object.keys(CONDITION_TYPE_LABELS) as SegmentConditionType[]

type PickerProps = {
  condition: SegmentCondition
  contactFields: { value: string; label: string }[]
  properties: { id: string; fieldKey: string; name: string }[]
  eventDefs: { id: string; eventName: string; displayName: string }[]
  onPick: (type: SegmentConditionType, field: string) => void
}

/** Một ô chọn 2 cấp: rê chuột vào "Thông tin khách / Thuộc tính / Sự kiện" → hiện trường con; bấm chọn là ghi cả loại lẫn trường. */
function ConditionFieldPicker({ condition: c, contactFields, properties, eventDefs, onPick }: PickerProps) {
  const { t } = useTranslation('cdp')
  const items: Record<SegmentConditionType, { value: string; label: string }[]> = {
    contact: contactFields,
    property: properties.map((p) => ({ value: p.fieldKey, label: p.name })),
    event: eventDefs.map((d) => ({ value: d.eventName, label: d.displayName })),
    lifecycle: [{ value: 'lifecycleStage', label: t('segments.currentStage') }],
  }
  const current = items[c.type]?.find((i) => i.value === c.field)?.label ?? (c.field || t('segments.pickField'))
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-1.5 text-left text-sm hover:border-primary/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
        >
          <span className="min-w-0 truncate">
            <span className="text-muted-foreground">{CONDITION_TYPE_LABELS[c.type]} · </span>
            {current}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {CONDITION_TYPES.map((ty) => (
          <DropdownMenuSub key={ty}>
            <DropdownMenuSubTrigger className={c.type === ty ? 'font-medium text-primary' : ''}>
              {CONDITION_TYPE_LABELS[ty]}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-72 min-w-[200px] overflow-y-auto">
              {items[ty].length === 0 ? (
                <DropdownMenuItem disabled>{t('segments.noItems')}</DropdownMenuItem>
              ) : (
                items[ty].map((i) => (
                  <DropdownMenuItem
                    key={i.value}
                    className={c.type === ty && c.field === i.value ? 'font-medium text-primary' : ''}
                    onSelect={() => onPick(ty, i.value)}
                  >
                    {i.label}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
const PREVIEW_LIMIT = 20

function cloneGroups(groups: SegmentConditionGroup[]): SegmentConditionGroup[] {
  return groups.map((g) => ({
    logic: g.logic,
    conditions: g.conditions.map((c) => ({ ...c })),
  }))
}

function conditionSummary(c: SegmentCondition, fieldLabel: (c: SegmentCondition) => string): string {
  const op = SEGMENT_OPERATORS.find((o) => o.value === c.operator)
  const noValue = op?.noValue
  if (c.type === 'lifecycle') return i18n.t('cdp:segments.stageEquals', { stage: stageLabel(String(c.value)) })
  const val = c.field === 'lifecycleStage' ? stageLabel(String(c.value)) : String(c.value)
  return `${fieldLabel(c)} ${operatorLabel(c.operator)}${noValue ? '' : ` ${val}`}`
}

export function SegmentsTab() {
  const { t } = useTranslation('cdp')
  const segmentsQuery = useSegments()
  const propsQuery = useCdpProperties()
  const tagsQuery = useTags()
  const lifecycleStages = useLifecycleStages()
  const defsQuery = useEventDefinitions()
  const createSeg = useCreateSegment()
  const updateSeg = useUpdateSegment()
  const deleteSeg = useDeleteSegment()
  const recalc = useRecalculateSegment()
  // Quản lý / Nhân viên mặc định chỉ có `cdp.view` → không hiện Tạo / Tính lại / Sửa / Xoá
  // (backend đòi cdp.create / cdp.update — kể cả "Tính lại" — / cdp.delete).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('cdp.create')
  const canUpdate = !!perms?.has('cdp.update')
  const canDelete = !!perms?.has('cdp.delete')

  const [builderOpen, setBuilderOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Segment | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [groups, setGroups] = useState<SegmentConditionGroup[]>([emptyGroup()])
  const [deleteTarget, setDeleteTarget] = useState<Segment | null>(null)
  const [previewTarget, setPreviewTarget] = useState<Segment | null>(null)
  const [recalcId, setRecalcId] = useState<string | null>(null)

  const properties = propsQuery.data?.properties ?? []
  const eventDefs = defsQuery.data ?? []

  const fieldLabel = useMemo(
    () => (c: SegmentCondition) => {
      if (c.type === 'contact') return CONTACT_FIELDS.find((f) => f.value === c.field)?.label ?? c.field
      if (c.type === 'property') return properties.find((p) => p.fieldKey === c.field)?.name ?? c.field
      if (c.type === 'event') return eventDefs.find((d) => d.eventName === c.field)?.displayName ?? c.field
      return t('contactFields.lifecycleStage')
    },
    [properties, eventDefs, t],
  )

  const aiDraft = useSegmentAiDraft()
  const [aiOpen, setAiOpen] = useState(true)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiResult, setAiResult] = useState<{ summary: string; questions: string[]; warnings: string[] } | null>(null)
  function runAi() {
    const prompt = aiPrompt.trim()
    if (!prompt) return
    aiDraft.mutate(
      { prompt, current: editTarget ? { name, description, groups } : null },
      {
        onSuccess: (d) => {
          setName(d.name || name)
          setDescription(d.description || description)
          if (d.groups.length) setGroups(cloneGroups(d.groups))
          setAiResult({ summary: d.summary, questions: d.questions, warnings: d.warnings })
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  function openCreate() {
    setAiPrompt('')
    setAiResult(null)
    setAiOpen(true)
    setEditTarget(null)
    setName('')
    setDescription('')
    setGroups([emptyGroup()])
    setBuilderOpen(true)
  }

  // Mở sẵn hộp tạo segment khi được dẫn từ nơi khác (`?tao=1`), rồi bỏ tham số để F5 không mở lại.
  // Chờ tải xong quyền; không có cdp.create thì chỉ bỏ tham số.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('tao') !== '1' || !perms) return
    if (perms.has('cdp.create')) openCreate()
    const next = new URLSearchParams(searchParams)
    next.delete('tao')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms])

  function openEdit(s: Segment) {
    setAiPrompt('')
    setAiResult(null)
    setAiOpen(true)
    setEditTarget(s)
    setName(s.name)
    setDescription(s.description ?? '')
    setGroups(Array.isArray(s.conditions) && s.conditions.length ? cloneGroups(s.conditions) : [emptyGroup()])
    setBuilderOpen(true)
  }

  function patchCondition(gi: number, ci: number, patch: Partial<SegmentCondition>) {
    setGroups((gs) =>
      gs.map((g, i) =>
        i !== gi
          ? g
          : {
              ...g,
              conditions: g.conditions.map((c, j) => (j !== ci ? c : { ...c, ...patch })),
            },
      ),
    )
  }

  function pickField(gi: number, ci: number, type: SegmentConditionType, field: string) {
    const cur = groups[gi]?.conditions[ci]
    if (cur && cur.type === type && cur.field === field) return
    const isTags = type === 'contact' && field === 'tags'
    const ops = isTags ? TAG_OPERATORS : operatorsFor(type)
    patchCondition(gi, ci, {
      type,
      field,
      operator: ops[0]?.value ?? 'equals',
      value: type === 'lifecycle' ? lifecycleStages.keys[0] : '',
    })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const n = name.trim()
    if (!n) {
      toast.error(t('segments.toast.nameRequired'))
      return
    }
    // Chuẩn hoá: bỏ điều kiện thiếu trường, ép số cho toán tử số
    const cleaned: SegmentConditionGroup[] = groups
      .map((g) => ({
        logic: g.logic,
        conditions: g.conditions
          .filter((c) => c.field)
          .map((c) => {
            const op = SEGMENT_OPERATORS.find((o) => o.value === c.operator)
            if (op?.noValue) return { ...c, value: '' }
            const numeric = ['gt', 'gte', 'lt', 'lte', 'event_count_gte'].includes(c.operator)
            return { ...c, value: numeric ? Number(c.value) : String(c.value) }
          }),
      }))
      .filter((g) => g.conditions.length > 0)
    if (!cleaned.length) {
      toast.error(t('segments.toast.needCondition'))
      return
    }
    const bad = cleaned.some((g) =>
      g.conditions.some((c) => {
        const op = SEGMENT_OPERATORS.find((o) => o.value === c.operator)
        if (op?.noValue) return false
        return c.value === '' || (typeof c.value === 'number' && Number.isNaN(c.value))
      }),
    )
    if (bad) {
      toast.error(t('segments.toast.missingValue'))
      return
    }
    try {
      if (editTarget) {
        await updateSeg.mutateAsync({
          id: editTarget.id,
          data: {
            name: n,
            description: description.trim(),
            conditions: cleaned,
          },
        })
        toast.success(t('segments.toast.updated'))
      } else {
        const created = await createSeg.mutateAsync({
          name: n,
          description: description.trim(),
          conditions: cleaned,
        })
        toast.success(t('segments.toast.created', { n: formatNumber(created.contactCount) }))
      }
      setBuilderOpen(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  function onRecalc(s: Segment) {
    setRecalcId(s.id)
    recalc.mutate(s.id, {
      onSuccess: (r) => toast.success(t('segments.toast.recalculated', { name: s.name, n: formatNumber(r.contactCount) })),
      onError: (err) => toast.error(apiError(err)),
      onSettled: () => setRecalcId(null),
    })
  }

  async function onDelete() {
    if (!deleteTarget) return
    try {
      await deleteSeg.mutateAsync(deleteTarget.id)
      toast.success(t('segments.toast.deleted'))
      setDeleteTarget(null)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const saving = createSeg.isPending || updateSeg.isPending
  const segments = segmentsQuery.data?.segments ?? []

  if (segmentsQuery.isLoading) return <Loading label={t('segments.loading')} />
  if (segmentsQuery.isError) return <QueryError error={segmentsQuery.error} what={t('segments.whatSegment')} />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('segments.summary', { count: segments.length })}
        </p>
        {canCreate && (
          <Button onClick={openCreate}>
            <Plus /> {t('segments.create')}
          </Button>
        )}
      </div>

      {segments.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('segments.empty.title')}
          description={t('segments.empty.description')}
          action={
            canCreate ? (
              <Button onClick={openCreate}>
                <Plus /> {t('segments.create')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {segments.map((s) => {
            const conds = Array.isArray(s.conditions) ? s.conditions : []
            return (
              <Card key={s.id}>
                <CardContent className="space-y-3 pt-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{s.name}</p>
                      {s.description && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{s.description}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-2xl font-bold tabular-nums">{formatNumber(s.contactCount)}</p>
                      <p className="text-xs text-muted-foreground">{t('segments.customers')}</p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    {conds.map((g, gi) => (
                      <div key={gi} className="flex flex-wrap items-center gap-1">
                        {gi > 0 && <span className="mr-1 text-xs font-semibold text-muted-foreground">{t('segments.andUpper')}</span>}
                        {g.conditions.map((c, ci) => (
                          <span key={ci} className="flex items-center gap-1">
                            {ci > 0 && (
                              <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                                {g.logic === 'OR' ? t('segments.orLower') : t('segments.andLower')}
                              </span>
                            )}
                            <Badge variant="secondary" className="font-normal">
                              {conditionSummary(c, fieldLabel)}
                            </Badge>
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <span className="text-xs text-muted-foreground">{t('segments.calculatedAt', { time: formatDateTime(s.lastCalculatedAt) })}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setPreviewTarget(s)}>
                        <Users className="!size-3.5" /> {t('segments.viewContacts')}
                      </Button>
                      {canUpdate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={recalc.isPending && recalcId === s.id}
                          onClick={() => onRecalc(s)}
                        >
                          <RefreshCw
                            className={recalc.isPending && recalcId === s.id ? '!size-3.5 animate-spin' : '!size-3.5'}
                          />
                          {t('segments.recalculate')}
                        </Button>
                      )}
                      {canUpdate && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(s)}
                          aria-label={t('segments.edit')}
                        >
                          <Pencil className="!size-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(s)}
                          aria-label={t('segments.delete')}
                        >
                          <Trash2 className="!size-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Builder */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editTarget ? t('segments.builder.editTitle') : t('segments.create')}</DialogTitle>
            <DialogDescription>
              {t('segments.builder.description')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            {(
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <button type="button" className="flex w-full items-center gap-2 text-left text-sm font-medium" onClick={() => setAiOpen((o) => !o)}>
                  <Sparkles className="h-4 w-4 text-primary" /> {editTarget ? t('segments.ai.editHeading') : t('segments.ai.createHeading')}
                  <ChevronDown className={cn('ml-auto h-4 w-4 transition-transform', aiOpen && 'rotate-180')} />
                </button>
                {aiOpen && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      rows={3}
                      maxLength={1500}
                      placeholder={editTarget ? t('segments.ai.editPlaceholder') : t('segments.ai.createPlaceholder')}
                      className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
                    />
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" onClick={runAi} disabled={aiDraft.isPending || !aiPrompt.trim()}>
                        {aiDraft.isPending ? <Loader2 className="!size-3.5 animate-spin" /> : <Sparkles className="!size-3.5" />} {editTarget ? t('segments.ai.applyEdit') : t('segments.ai.createDraft')}
                      </Button>
                      <span className="text-xs text-muted-foreground">{t('segments.ai.hint')}</span>
                    </div>
                    {aiResult && (
                      <div className="space-y-1 rounded-md bg-card p-2 text-xs">
                        {aiResult.summary && <p>{aiResult.summary}</p>}
                        {aiResult.questions.map((q, i) => <p key={i} className="text-amber-700">❓ {q}</p>)}
                        {aiResult.warnings.map((w, i) => <p key={i} className="text-amber-700">⚠ {w}</p>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('segments.builder.nameLabel')}>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('segments.builder.namePlaceholder')}
                  autoFocus
                />
              </Field>
              <Field label={t('segments.builder.descLabel')}>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('segments.builder.descPlaceholder')} />
              </Field>
            </div>

            <div className="space-y-3">
              {groups.map((g, gi) => (
                <div key={gi} className="space-y-2">
                  {gi > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-border" />
                      <Badge variant="outline">{t('segments.andUpper')}</Badge>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                  )}
                  <div className="space-y-2 rounded-lg border bg-muted/30 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{t('segments.builder.groupN', { n: gi + 1 })}</span>
                        <Select
                          value={g.logic}
                          onValueChange={(v) =>
                            setGroups((gs) => gs.map((x, i) => (i === gi ? { ...x, logic: v as 'AND' | 'OR' } : x)))
                          }
                        >
                          <SelectTrigger className="h-7 w-[150px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">{t('segments.builder.logicAnd')}</SelectItem>
                            <SelectItem value="OR">{t('segments.builder.logicOr')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {groups.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setGroups((gs) => gs.filter((_, i) => i !== gi))}
                        >
                          <X className="!size-3.5" /> {t('segments.builder.removeGroup')}
                        </Button>
                      )}
                    </div>

                    {g.conditions.map((c, ci) => {
                      const isTagField = c.type === 'contact' && c.field === 'tags'
                      const ops = isTagField ? TAG_OPERATORS : operatorsFor(c.type)
                      const selProp = c.type === 'property' ? properties.find((p) => p.fieldKey === c.field) : undefined
                      const propOptions = selProp && ['single_select', 'multi_select'].includes(selProp.fieldType) ? selProp.options : null
                      const op = ops.find((o) => o.value === c.operator)
                      const contactField = CONTACT_FIELDS.find((f) => f.value === c.field)
                      const numeric =
                        ['gt', 'gte', 'lt', 'lte', 'event_count_gte'].includes(c.operator) || !!contactField?.numeric
                      const stageSelect =
                        c.type === 'lifecycle' || (c.type === 'contact' && c.field === 'lifecycleStage')
                      return (
                        <div key={ci} className="grid gap-2 sm:grid-cols-[minmax(0,1.3fr)_150px_1fr_auto]">
                          {/* Loại + trường: rê chuột vào nhóm → hiện trường con, bấm chọn là ghi cả hai */}
                          <ConditionFieldPicker
                            condition={c}
                            contactFields={CONTACT_FIELDS}
                            properties={properties}
                            eventDefs={eventDefs}
                            onPick={(type, field) => pickField(gi, ci, type, field)}
                          />

                          {/* Toán tử */}
                          <Select
                            value={c.operator}
                            // Radix bắn onValueChange('') khi danh sách toán tử đổi theo trường → bỏ qua giá trị rỗng.
                            onValueChange={(v) => v && patchCondition(gi, ci, { operator: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ops.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Giá trị */}
                          {op?.noValue ? (
                            <div />
                          ) : stageSelect ? (
                            <Select
                              value={String(c.value) || lifecycleStages.keys[0]}
                              onValueChange={(v) => patchCondition(gi, ci, { value: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {lifecycleStages.stages.map((s) => (
                                  <SelectItem key={s.key} value={s.key}>
                                    {s.label}
                                  </SelectItem>
                                ))}
                              <ManageStagesLink /></SelectContent>
                            </Select>
                          ) : isTagField && (tagsQuery.data?.length ?? 0) > 0 ? (
                            <Select value={String(c.value)} onValueChange={(v) => patchCondition(gi, ci, { value: v })}>
                              <SelectTrigger>
                                <SelectValue placeholder={t('segments.builder.pickTag')} />
                              </SelectTrigger>
                              <SelectContent>
                                {tagsQuery.data!.map((tag) => (
                                  <SelectItem key={tag.id} value={tag.name}>
                                    {tag.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : propOptions && propOptions.length > 0 ? (
                            <Select value={String(c.value)} onValueChange={(v) => patchCondition(gi, ci, { value: v })}>
                              <SelectTrigger>
                                <SelectValue placeholder={t('segments.builder.pickValue')} />
                              </SelectTrigger>
                              <SelectContent>
                                {propOptions.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={numeric ? 'number' : 'text'}
                              value={String(c.value)}
                              onChange={(e) =>
                                patchCondition(gi, ci, {
                                  value: e.target.value,
                                })
                              }
                              placeholder={numeric ? '0' : t('segments.builder.value')}
                            />
                          )}

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            disabled={g.conditions.length <= 1}
                            onClick={() =>
                              setGroups((gs) =>
                                gs.map((x, i) =>
                                  i === gi
                                    ? {
                                        ...x,
                                        conditions: x.conditions.filter((_, j) => j !== ci),
                                      }
                                    : x,
                                ),
                              )
                            }
                            aria-label={t('segments.builder.removeCondition')}
                          >
                            <X className="!size-3.5" />
                          </Button>
                        </div>
                      )
                    })}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setGroups((gs) =>
                          gs.map((x, i) =>
                            i === gi
                              ? {
                                  ...x,
                                  conditions: [...x.conditions, emptyCondition()],
                                }
                              : x,
                          ),
                        )
                      }
                    >
                      <Plus className="!size-3.5" /> {t('segments.builder.addCondition')}
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setGroups((gs) => [...gs, emptyGroup()])}
              >
                <Plus className="!size-3.5" /> {t('segments.builder.addGroup')}
              </Button>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBuilderOpen(false)}>
                {t('segments.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="animate-spin" />}
                {editTarget ? t('segments.builder.saveRecalc') : t('segments.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Xem khách hàng trong segment */}
      <SegmentContactsDialog segment={previewTarget} onClose={() => setPreviewTarget(null)} />

      {/* Xóa */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('segments.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="segments.deleteDialog.description"
                values={{ name: deleteTarget?.name }}
                components={{ b: <span className="font-medium text-foreground" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('segments.cancel')}
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={deleteSeg.isPending}>
              {deleteSeg.isPending && <Loader2 className="animate-spin" />}
              {t('segments.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SegmentContactsDialog({ segment, onClose }: { segment: Segment | null; onClose: () => void }) {
  const { t } = useTranslation('cdp')
  const [page, setPage] = useState(1)
  const id = segment?.id ?? null
  const { data, isLoading, isError, error } = useSegmentContacts(id, PREVIEW_LIMIT, (page - 1) * PREVIEW_LIMIT)
  const { data: perms } = useMyPermissions()
  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    if (!id) return
    setExporting(true)
    try {
      const truncated = await downloadSegmentCsv(id)
      if (truncated) toast.warning(t('segments.contacts.truncated'))
      else toast.success(t('segments.contacts.exported'))
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setExporting(false)
    }
  }

  const columns: Column<SegmentContact>[] = [
    {
      key: 'name',
      header: t('segments.contacts.colCustomer'),
      cell: (c) => (
        <Link to={`/customers/${c.id}`} className="flex items-center gap-2 hover:underline" onClick={onClose}>
          <Avatar className="h-7 w-7">
            {c.avatarUrl && <AvatarImage src={c.avatarUrl} alt={c.fullName ?? ''} />}
            <AvatarFallback className="text-[10px]">{initials(c.fullName)}</AvatarFallback>
          </Avatar>
          <span className="truncate font-medium">{c.fullName || t('segments.contacts.noName')}</span>
        </Link>
      ),
    },
    {
      key: 'phone',
      header: t('segments.contacts.colPhone'),
      cell: (c) => <span className="tabular-nums">{c.phone || '—'}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      cell: (c) => <span className="text-muted-foreground">{c.email || '—'}</span>,
    },
    {
      key: 'stage',
      header: t('segments.contacts.colStage'),
      cell: (c) => <StageBadge stage={c.lifecycleStage} />,
    },
  ]

  return (
    <Dialog
      open={!!segment}
      onOpenChange={(o) => {
        if (!o) {
          setPage(1)
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('segments.contacts.title', { name: segment?.name })}</DialogTitle>
          <DialogDescription>
            {t('segments.contacts.description')}
          </DialogDescription>
          {perms?.has('contacts.export') && (
            <div>
              <Button variant="outline" size="sm" onClick={onExport} disabled={exporting || !data?.total}>
                <Download className="!size-3.5" /> {exporting ? t('segments.contacts.exporting') : t('segments.contacts.exportCsv')}
              </Button>
            </div>
          )}
        </DialogHeader>
        {isError ? (
          <QueryError error={error} what={t('segments.contacts.what')} />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={data?.contacts ?? []}
              loading={isLoading}
              rowKey={(c) => c.id}
              emptyTitle={t('segments.contacts.empty')}
            />
            {!!data && data.total > PREVIEW_LIMIT && (
              <Pagination page={page} limit={PREVIEW_LIMIT} total={data.total} onPageChange={setPage} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
